import { injectable, injectFromBase } from 'inversify'
import { DomainSettings } from '@/decorators/domainDecorators'
import DomainBaseService from '@/services/domain/domainBaseService'
import type ProgressRepository from '@/data/repositories/progressRepository'
import Progress from '@/data/models/Progress'
import type { ProgressIdentifier, ProgressType } from '@/services/types'
import type { RequiredBy } from '@/types'
import type { ServerProgressEventPayload } from '@/services/transport/serverEvents'

@DomainSettings({
  model: Progress,
})
@injectable()
@injectFromBase()
export default class ProgressService extends DomainBaseService<ProgressRepository> {
  public insertOrUpdateProgress(
    progress: RequiredBy<Pick<Progress, 'type' | 'originalId' | 'progress'>, 'type' | 'originalId'>,
  ) {
    this.getDomainRepository().insertOrUpdateRaw([
      {
        ...progress,
        id: this.getProgressId({
          type: progress.type,
          originalId: progress.originalId,
        }),
      },
    ])
  }

  public findProgressOrNull(id: ProgressIdentifier) {
    return this.findEntityByIdOrNull(this.getProgressId(id))
  }

  public deleteProgress(id: ProgressIdentifier) {
    return this.deleteFromStore([this.getProgressId(id)])
  }

  public isLastProgress(progress: Omit<Progress, 'id'>) {
    const currentProgress = this.findProgressOrNull(progress)

    const progressPrecentage = progress.progress
    const currentProgressPrecentage = currentProgress?.progress

    return currentProgressPrecentage !== 100 && progressPrecentage === 100
  }

  public mapServerEventPayload(payload: ServerProgressEventPayload, progressType: ProgressType): Omit<Progress, 'id'> {
    if ('entityId' in payload) {
      return {
        type: progressType,
        originalId: String(payload.entityId),
        progress: payload.percentageValue,
      } as Progress
    }

    return {
      type: progressType,
      originalId: String(payload.id),
      progress: payload.progress,
    } as Progress
  }

  protected getProgressId({ type, originalId }: ProgressIdentifier) {
    return `${type}-${originalId}`
  }
}
